import { Component, Inject, PLATFORM_ID, ViewChild } from '@angular/core';
import { App, IonicPage, NavController, NavParams, Platform } from 'ionic-angular';
import { NavRootPage } from '../../nav/nav-root/nav-root';
import {
  UserSelectOptions,
  VesselMetadata,
  VesselOption,
  VesselSelectOptions
} from '../../../models/setup/setup.models';
import { OperationService } from '../../../services/operations/operation/operation.service';
import { NavHelperService } from '../../../services/nav/nav-helper.service';
import { VesselService } from '../../../services/vessel/vessel.service';
import { NavMainMenuPage } from '../../nav/nav-menus/nav-main-menu/nav-main-menu';
import { AuthenticationRequest } from '../../../_foundation/models/security.models';
import { AuthService } from '../../../_foundation/services/auth/auth.service';
import { isPlatformBrowser } from '@angular/common';
import { ResetPasscodePage } from '../reset-passcode/reset-passcode';
import { UserService } from '../../../services/user/user.service';
import { KnownUser, KnownUserMapping, UserOption } from '../../../models/user-models';
import { MetadataService } from '../../../services/metadata/metadata-service.service';
import { cloneDeep } from 'lodash';
import { IdleService } from '../../../services/idle/idle.service';
import { DefaultPage } from '../../default/default-page/default-page';
import { DailyLogPage } from '../../daily-log/daily-log/daily-log';
import { ImplementerNavigatorPage } from '../../implementer/implementer-navigator/implementer-navigator';
import { SetupService } from '../../../services/setup/setup.service';
import { PermissionsService } from '../../../services/permissions/permissions.service';
import { AppConfig } from '../../../app/app.config';
import { PtLuxonDate } from '../../../models/projects/date.models';
import { VesselContextService } from '../../../services/vessel/vessel-context.service';
import { SupportContextKey } from '../../../models/support/support.models';
import { SupportContextService } from '../../../services/support/support-context.service';

@IonicPage()
@Component({
  selector: 'page-login',
  templateUrl: 'login.html',
})
export class LoginPage {
  @ViewChild('usernameInput') usernameInput;
  @ViewChild('passwordInput') passwordInput;

  config = AppConfig.settings;
  selectedUserId: number;
  selectedUsername: string;
  model: AuthenticationRequest = new AuthenticationRequest();
  failed = false;
  failedMessage: string = 'Login failed.';
  loggedIn = false;
  editor: VesselOption;
  editorPrevious: VesselOption;
  vesselSelectOptions: any = new VesselSelectOptions().options;
  userSelectOptions: any = new UserSelectOptions().options;
  vesselOptions: VesselOption[] = [];
  currentVesselName: string;
  currentVessel: VesselOption;
  userOptions: UserOption[] = [];
  loading: boolean;
  loggingIn: boolean;
  currentTimeChanged: PtLuxonDate;
  isWeb: boolean;
  sessionTimeout: boolean;

  metadata: VesselMetadata = new VesselMetadata();
  metadataLoaded: boolean;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    public navCtrl: NavController,
    public navParams: NavParams,
    public vesselService: VesselService,
    public operationService: OperationService,
    public platform: Platform,
    private app: App,
    public navHelperService: NavHelperService,
    private authService: AuthService,
    private userService: UserService,
    private metaDataService: MetadataService,
    private idleService: IdleService,
    private vesselContextService: VesselContextService,
    private supportContextService: SupportContextService,
    private setupService: SetupService,
    private permissionsService: PermissionsService
  ) {
  }

  ionViewWillLoad() {
    this.loading = true;
  }

  async ionViewDidLoad() {
    this.supportContextService.setComponentContext(SupportContextKey.Login, 'Login');

    this.isWeb = this.platform.is('core');
    this.sessionTimeout = this.navParams.get('sessionTimeout');
    this.currentTimeChanged = new PtLuxonDate();

    const configuredVesselName =
      (this.config.vessel && this.config.vessel.trim() !== '') ? this.config.vessel.trim() : null;

    // If no configured vessel, allow selection and clear stored setup
    if (!configuredVesselName) {
      this.currentVesselName = null;
      this.vesselService.deleteSetup();
    } else {
      this.currentVesselName = configuredVesselName;
    }

    this.model.isImplementerEnvironment = this.config.isImplementerEnvironment;
    this.metadataLoaded = false;
    this.loading = true;

    let metadata: VesselMetadata | null = null;

    try {
      metadata = await this.vesselService.getVesselMetadata();
    } catch {
      metadata = await this.vesselService.getLocalVessels();
    }

    if (!metadata) {
      // nothing we can do, app likely unusable anyway
      this.loading = false;
      return;
    }

    this.metadata = metadata;
    this.vesselOptions = metadata.vesselOptions || [];
    await this.persistVesselSelectionFromMetadata(metadata);

    // Load current editor (stored vessel selection)
    await this.getEditor(metadata);

    if (configuredVesselName) {
      // Rule 1: force configured vessel if it exists, then disable dropdown
      const match = this.vesselOptions.find(v => v.name?.toUpperCase() === configuredVesselName.toUpperCase());
      if (match) {
        this.currentVessel = match;
        this.editor = cloneDeep(match);
        await this.vesselService.saveSetup(this.editor);
        this.vesselContextService.setVesselId(this.editor.id);
      }
      this.currentVesselName = configuredVesselName;
    } else {
      // Rule 3: refresh stored setup from metadata (only when no forced vessel)
      await this.refreshStoredVesselFromMetadata(metadata);
    }

    this.metadataLoaded = true;
    this.loading = false;

    // Local users etc as before
    this.userOptions = await this.userService.getLocalUsers();
    this.metaDataService.getMetadata().catch();
  }

  async getEditor(metadata: any) {
    this.loggedIn = false;

    if (isPlatformBrowser(this.platformId)) {
      this.model.username = this.authService.latestUsername();
      this.model.remember = !!this.model.username;
    }

    const editor = await this.vesselService.getEditor();

    this.editor = !editor ? new VesselOption() : editor;
    this.editorPrevious = Object.assign({}, this.editor);
    this.vesselOptions = metadata.vesselOptions;
    this.loading = false;
  }

  keydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.login();
    }
  }

  async login() {
    this.failed = false;
    this.loggingIn = true;
    const previousUserId = this.navParams.get('previousUser');

    if (this.model.username == null) {
      this.failedMessage = `'Username' is required`;
    } else if (this.model.password == null) {
      this.failedMessage = `'Passcode' is required`;
    } else {
      this.failedMessage = `Username or passcode is incorrect`;
    }

    const versionNumberChanged = await this.userService.updateVersionNumber();

    if (versionNumberChanged) {
      // re-save vessel metadata if changed, as this will have just been deleted
      await this.vesselService.saveSetup(this.editor);
      this.vesselContextService.setVesselId(this.editor.id);
    }

    this.authService
      .login(this.model)
      .subscribe(res => {
        if (res == null) {
          return this.failed = true;
        }

        if (res.error != null) {
          return this.failed = true;
        }

        this.loggingIn = false;
        this.loggedIn = true;

        let knownUser = new KnownUser();
        knownUser.userId = res.user.id;

        this.userService.getUserById(knownUser.userId)
          .then(user => {
            if (!!user) {
              knownUser = user;
            }

            knownUser.disabled = res.user.disabled;
            knownUser.emailAddress = res.user.emailAddress;
            knownUser.firstName = res.user.firstName;
            knownUser.lastName = res.user.lastName;
            knownUser.roles = res.user.roles;
            knownUser.username = res.user.username;

            const userMapping = new KnownUserMapping();
            userMapping.username = knownUser.username;

            if (!user) {
              this.userService.getKnownUserTempId()
                .then(id => {
                  knownUser.id = id;
                  userMapping.userId = id;
                  this.userService.saveKnownUser(knownUser);
                });
            } else {
              userMapping.userId = knownUser.id;
              this.userService.saveKnownUser(knownUser);
              this.userService.saveKnownUserMapping(userMapping);
            }
          });

        if (previousUserId != null) {
          this.idleService.reset();
          // If its the same user logging in go back to where they were. unless version number changed then reset.
          if (this.isSameUser(previousUserId, knownUser.userId) && !versionNumberChanged) {
            this.resumePreviousSession();
          } else {
            // A different user starts at the normal landing page.
            this.navigateToDefaultPage();
          }
        } else if (!res.user.resetNeeded) {
          this.navigateToDefaultPage();
          this.idleService.reset();
        } else {
          this.navCtrl.push(ResetPasscodePage, {user: res.user});
        }
      }, (err) => {
        this.loggingIn = false;

        if (err.error === 'UserDisabled') {
          this.userService
            .getKnownUserByUsername(this.model.username)
            .then(user => {
              if (user) {
                user.disabled = true;
                this.userService.saveKnownUser(user);
              }
            });

          this.failedMessage = 'This user has been made inactive by an administrator.';
          this.failed = true;
          return;
        } else if (err.error === 'UserLocked') {
          this.userService
            .getKnownUserByUsername(this.model.username)
            .then(user => {
              if (user) {
                user.locked = true;
                this.userService.saveKnownUser(user);
              }
            });

          this.failedMessage = 'This user has been locked.';
          this.failed = true;
          return;
        } else if (err.error === 'InvalidImplementerPermission') {
          this.userService
            .getKnownUserByUsername(this.model.username)
            .then(user => {
              if (user) {
                this.userService.saveKnownUser(user);
              }
            });

          this.failedMessage = 'This user does not have permission to access the Locked Day site';
          this.failed = true;
          return;
        }

        // check local storage for token
        this.userService
          .getKnownUserByUsername(this.model.username)
          .then(user => {
            if (!user) {
              this.failed = true;
            } else {
              this.loggedIn = true;
              this.idleService.reset();

              const localUserId = user.userId != null ? user.userId : user.id;
              if (previousUserId != null && this.isSameUser(previousUserId, localUserId) && !versionNumberChanged) {
                this.resumePreviousSession();
              } else {
                this.navigateToDefaultPage();
              }
            }
          });
      });
  }

  async vesselChanged(id: any) {
    this.editor.id = cloneDeep(id);
    const newVessel = this.vesselOptions.find(e => e.id === id);
    if (newVessel) {
      this.editor = cloneDeep(newVessel);
    }

    await this.vesselService.saveSetup(this.editor);
    this.vesselContextService.setVesselId(id);
  }

  userChanged(id: any) {
    this.userService
      .getKnownUser(id)
      .then(user => {

        if (!user) {
          return;
        }

        this.model.username = user.username;
        this.selectedUsername = this.model.username;
      });
  }

  resetUser() {
    if (this.model.username === this.selectedUsername) {
      return;
    }
    this.selectedUsername = null;
    this.selectedUserId = null;
  }

  focusUsernameInput() {
    this.usernameInput.setFocus();
  }

  focusPasswordInput() {
    this.passwordInput.setFocus();
  }

  // we re-save the vessel editor to ensure metadata is up to date from any api changes
  private async refreshStoredVesselFromMetadata(metadata: VesselMetadata): Promise<void> {
    const stored = await this.vesselService.getEditor();
    if (!stored?.id) {
      // No stored selection, nothing to refresh
      return;
    }

    const fresh = (metadata?.vesselOptions || []).find(v => v.id === stored.id);
    if (!fresh) {
      return;
    }

    await this.vesselService.saveSetup(cloneDeep(fresh));
    this.vesselContextService.setVesselId(fresh.id);

    this.editor = cloneDeep(fresh);
    this.currentVessel = fresh;
    this.currentVesselName = fresh.name;
  }

  private async persistVesselSelectionFromMetadata(metadata: VesselMetadata): Promise<void> {
    const options = metadata?.vesselOptions || [];

    const configuredName =
      (this.config.vessel && this.config.vessel.trim() !== '') ? this.config.vessel.trim() : null;

    let target: VesselOption | null = null;

    if (configuredName) {
      target = options.find(v => (v.name || '').toUpperCase() === configuredName.toUpperCase()) || null;
    } else {
      const stored = await this.vesselService.getEditor();
      if (stored?.id) {
        target = options.find(v => v.id === stored.id) || null;
      }
    }

    if (!target) {
      return;
    }

    // Always persist the latest object shape/fields (eg legsCount) every load
    await this.vesselService.saveSetup(cloneDeep(target));
    this.vesselContextService.setVesselId(target.id);

    // Keep UI state aligned too
    this.editor = cloneDeep(target);
    this.currentVessel = target;
    this.currentVesselName = target.name;
  }

  private isSameUser(previousUserId: any, currentUserId: any): boolean {
    if (previousUserId == null || currentUserId == null) {
      return false;
    }

    return `${previousUserId}` === `${currentUserId}`;
  }

  private resumePreviousSession() {
    const loginNav = this.getLoginNav();

    if (loginNav.length() > 1) {
      this.supportContextService.restoreCurrentNavContext();
      loginNav.pop().then();
      return;
    }

    this.navigateToDefaultPage();
  }

  private async navigateToDefaultPage() {
    this.loggingIn = true;
    let page: any = this.config.isImplementerEnvironment ? ImplementerNavigatorPage : DefaultPage;

    try {
      const setupComplete = await this.setupService.checkInitialSetupComplete();
      // DefaultPage normally initializes this flag for the sidebar.
      this.navHelperService.updateInitialSetupComplete(setupComplete);
      if (!this.config.isImplementerEnvironment && setupComplete) {
        const permissions = await this.permissionsService.sidebarPermissions(true);
        if (permissions.canClickDailyLog) {
          page = DailyLogPage;
        }
      }
    } catch {
      // Keep the mode's entry page when setup or access cannot be confirmed.
    }

    try {
      this.navHelperService.updateNavMenuPage({page: NavMainMenuPage});
      this.navHelperService.updateNavPage({page});
      await this.getLoginNav().setRoot(NavRootPage);
    } finally {
      this.loggingIn = false;
    }
  }

  private getLoginNav(): NavController {
    return this.app && this.app.getRootNav ? this.app.getRootNav() : this.navCtrl;
  }
}



// WEBPACK FOOTER //
// ./src/pages/auth/login/login.ts